import cx from "classnames";
import { fabric } from "fabric";
import { memo, useCallback, useEffect, useRef, useState } from "react";

import { CanvasPaperConfig } from "../designer/PageDesigner/PageDesigner";
import { CanvasManager } from "./CanvasManager";
import { createPageDividerLines } from "./page-dividers";
import { getNumberOfPages } from "./page-panning";
import { RenderContext } from "./renderer/fabric-renderer";
import { DocumentDesignState } from "./state/types";
import { rearrangeItems } from "./relative-position";
import { throttle } from "lodash";

export interface CanvasProps {
  context: RenderContext;
  dimensions: CanvasPaperConfig;
  state: DocumentDesignState;
  isPreviewing: boolean;
  onStateChange: (state: DocumentDesignState) => void;
  onSelectionChange: (selectedItemId: string | undefined) => void;
  onReady: (manager: CanvasManager) => void;
}

const CanvasRaw: React.FC<CanvasProps> = ({
  context,
  dimensions,
  state,
  isPreviewing,
  onStateChange,
  onSelectionChange,
  onReady,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const managerRef = useRef<CanvasManager>();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [scrollHeight, setScrollHeight] = useState("100%");

  const handleAfterRender = useCallback(() => {
    if (managerRef.current) {
      const newScrollHeight =
        getNumberOfPages(managerRef.current.canvas, dimensions) *
        dimensions.height;
      setScrollHeight(`${newScrollHeight}px`);
    }
  }, [dimensions]);

  useEffect(() => {
    if (!canvasElRef.current) {
      return;
    }

    const manager = new CanvasManager(canvasElRef.current);
    managerRef.current = manager;

    return () => {
      manager.destroy();
    };
  }, []);

  useEffect(() => {
    managerRef.current?.setEvents({
      onStateChange,
      onSelectionChange,
      onReady,
      onAfterRender: handleAfterRender,
    });
  }, [onStateChange, onSelectionChange, onReady, handleAfterRender]);

  useEffect(() => {
    const canvas = managerRef.current?.canvas;
    if (!wrapperRef.current || !scrollerRef.current || !canvas) {
      return;
    }

    let canvasHeight = wrapperRef.current.clientHeight;
    let scrollerHeight = scrollerRef.current.clientHeight;
    let pageDividers: fabric.Line[] = [];

    const updateCanvasSize = throttle(
      () => {
        canvas.setWidth(dimensions.width);

        if (scrollerHeight < canvasHeight) {
          canvas.setHeight(scrollerHeight);
        } else {
          canvas.setHeight(canvasHeight);
        }
      },
      16,
      { leading: false, trailing: true },
    );

    const canvasResizeObserver = new ResizeObserver((entries) => {
      canvasHeight = entries[0].contentRect.height;
      updateCanvasSize();
    });
    canvasResizeObserver.observe(wrapperRef.current);

    const scrollerResizeObserver = new ResizeObserver((entries) => {
      scrollerHeight = entries[0].contentRect.height;
      updateCanvasSize();

      canvas.remove(...pageDividers);
      pageDividers = createPageDividerLines(canvas, dimensions);
    });
    scrollerResizeObserver.observe(scrollerRef.current);

    return () => {
      canvas.remove(...pageDividers);
      canvasResizeObserver.disconnect();
      scrollerResizeObserver.disconnect();
    };
  }, [dimensions]);

  useEffect(() => {
    if (context.allLinkedRecordsFetched) {
      managerRef.current?.render(state, context);
    }
  }, [state, context]);

  useEffect(() => {
    const canvas = managerRef.current?.canvas;
    if (canvas && canvas._objects.length > 1) {
      onStateChange(rearrangeItems(canvas, state));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.record?.id, context.loading]);

  useEffect(() => {
    managerRef.current?.setIsPreviewing(isPreviewing);
  }, [isPreviewing]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>): void => {
      const canvas = managerRef.current?.canvas;
      if (!canvas) return;

      const vpt = canvas.viewportTransform;
      if (vpt) {
        vpt[5] = -event.currentTarget.scrollTop;
        canvas.setViewportTransform(vpt);
        canvas.requestRenderAll();
      }
    },
    [],
  );

  const classes = cx("canvas", {
    "canvas--previewing": isPreviewing,
  });

  return (
    <div ref={wrapperRef} className={classes} onScroll={handleScroll}>
      <div
        ref={scrollerRef}
        className="canvas__scroller"
        style={{ height: scrollHeight }}
      >
        <div className="canvas__wrapper">
          <canvas ref={canvasElRef} className="canvas__body" />
        </div>
      </div>
    </div>
  );
};

export const Canvas = memo(CanvasRaw);
